// Recharts
import { Bar, CartesianGrid, ComposedChart, Customized, ReferenceDot, ReferenceLine, XAxis, YAxis } from "recharts";

// Helpers
import {
	accumulatedValue,
	getAccumulativeValue,
	getProjectedProperty,
	getTransformedData,
	getValueFromData,
} from "@DataPages/helpers/salesOperations/salesLog/utils";

// Third party
import moment from "moment";

// Services
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";

// Components
import CustomizedYTick from "@DataPages/components/SalesOperations/Tabs/SalesLog/Widgets/AccumulativeChart/CustomizedYTick.tsx";
import CustomXTick from "@DataPages/components/SalesOperations/Tabs/SalesLog/Widgets/AccumulativeChart/CustomXTick.tsx";
import CustomYTick from "@DataPages/components/SalesOperations/Tabs/SalesLog/Widgets/AccumulativeChart/CustomYTick.tsx";
import CandleStickBar from "@DataPages/components/SalesOperations/Tabs/SalesLog/Widgets/AccumulativeChart/CandleStickBar.tsx";
import CustomizedReferenceDot from "@DataPages/components/SalesOperations/Tabs/SalesLog/Widgets/AccumulativeChart/CustomizedReferenceDot.tsx";

// Types
import { CategoricalChartState } from "recharts/types/chart/types";
import { ISalesAnalyticsItem } from "@DataPages/types/salesOperations/salesLog/model";
import { MixedType, TDataProperty } from "@DataPages/types/salesOperations/salesLog/utils";
import { Stack, Typography } from "@mui/material";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

type SalesLogAccumulativeChartProps = {
	id: string;
	activeDay: number;
	yAxisLabel: string;
	chartIndex: number;
	data: MixedType[] | ISalesAnalyticsItem[];
	chartTitle: string;
	dataProperty: TDataProperty;
	unitType: string;
	selectedCommissionCycle: ICommissionCycle;
	lastActiveCommissionCycle: ICommissionCycle;
	isHoveringChart: boolean;
	setActiveDay: (day: number) => void;
	setIsHoveringChart: (value: boolean) => void;
	setSpeedometerValue: (value: number) => void;
};

const NoDataLabel = () => {
	return (
		<svg>
			<text x={380} y={180} textAnchor="middle" fontFamily="Montserrat" fontSize="20" fontWeight={500} fill="#666666">
				No Data Available
			</text>
		</svg>
	);
};

/*************************************************** HELPERS ************************************************/
const isDateBeforeCurrentDate = (referenceDate: Date, day: number) => {
	const newDate = moment(`${moment(referenceDate).format(DATE_FORMAT.yearMonth)}-${day}`);

	return newDate.isBefore(moment());
};

function adjustNegativeNumber(inputNumber: number) {
	if (inputNumber < 0) {
		// Define a factor for other negative numbers, e.g., reducing the number by 60%
		const factor = 0.6;
		return inputNumber - inputNumber * factor;
	}
	return inputNumber;
}

// Y-Axis Range Domain
function getYAxisRange(dataMin: number, dataPropertyValue: string) {
	if (dataPropertyValue === "deliveredToday") {
		return dataMin;
	} else {
		return dataMin + adjustNegativeNumber(dataMin);
	}
}

export default function SalesLogAccumulativeChart({
	activeDay,
	setActiveDay,
	yAxisLabel,
	chartIndex,
	data,
	chartTitle,
	dataProperty,
	unitType,
	selectedCommissionCycle,
	lastActiveCommissionCycle,
	isHoveringChart,
	setIsHoveringChart,
	setSpeedometerValue,
}: Readonly<SalesLogAccumulativeChartProps>) {
	const daysInCurrentMonth = moment(selectedCommissionCycle?.endDate).daysInMonth();
	const isLastDayOfMonth = moment().endOf("month").isSame(moment(), "day");

	const { filteredRows, tabSelected } = useSalesLogChart();

	/************************************************* HELPERS **************************************************/
	const transformedData = getTransformedData({
		data,
		daysInCurrentMonth,
		dataProperty,
		chartIndex,
		selected: selectedCommissionCycle,
		tabSelected,
		lastActiveCommissionCycle,
		isLastDayOfMonth,
	});

	const getReferenceLineValue = () => {
		let result;
		if (
			!isLastDayOfMonth &&
			!isHoveringChart &&
			moment(selectedCommissionCycle?.startDate).isBetween(
				lastActiveCommissionCycle?.startDate,
				lastActiveCommissionCycle?.endDate,
				null,
				"[]"
			) &&
			!moment(selectedCommissionCycle?.endDate).isBefore(moment())
		) {
			if (chartIndex === 0) {
				result = transformedData[moment().date() - 1]?.["projection"]?.["projected"]?.[getProjectedProperty(tabSelected)];
			} else if (chartIndex === 1) {
				result = transformedData[moment().date() - 1]?.["summary"]?.["salesPace"];
			}
		}

		return result ?? getAccumulativeValue({ chartIndex, transformedData, activeDay, tabSelected });
	};

	/*********************************************** HANDLERS ***********************************************/
	const handleOnMouseMove = (values: CategoricalChartState) => {
		const currentActiveDay = values?.activePayload?.[0]?.payload?.day;
		setActiveDay(currentActiveDay);

		let gross = transformedData[currentActiveDay - 1]?.["daily"]?.[getProjectedProperty(tabSelected)];
		let units = transformedData[currentActiveDay - 1]?.["summary"]?.["deliveredToday"];

		if (currentActiveDay === daysInCurrentMonth) {
			gross = data[currentActiveDay - 1]?.["daily"]?.[getProjectedProperty(tabSelected)];
			units = data[currentActiveDay - 1]?.["summary"]?.["deliveredToday"];
		}

		let result = 0;
		result = gross / units;
		setSpeedometerValue(result ? Math.round(result) : 0);
	};

	const handleOnMouseEnter = () => {
		setIsHoveringChart(true);
	};

	const handleOnMouseOut = () => {
		setActiveDay(daysInCurrentMonth);
		setIsHoveringChart(false);
		setSpeedometerValue(0);
	};

	return (
		<Stack>
			<Typography
				component={"div"}
				paddingLeft={12}
				marginBottom={-2}
				variant="h6"
				className={"!font-[Montserrat] font-medium text-[#666666] !text-[18px]"}
			>
				{chartTitle}
			</Typography>
			<ComposedChart
				syncId={"syncId"}
				onMouseMove={handleOnMouseMove}
				onMouseLeave={handleOnMouseOut}
				onMouseEnter={handleOnMouseEnter}
				width={700}
				height={360}
				data={transformedData}
				margin={{ top: 40, right: 20, left: 35, bottom: 20 }}
			>
				<CartesianGrid stroke="#F2F2F2" strokeDasharray="3 3" />

				<YAxis
					label={{
						value: yAxisLabel,
						angle: -90,
						position: "left",
						dx: 15,
						dy: -40,
						fill: "#666666",
						fontSize: "12",
						fontWeight: "600",
						fontFamily: "Montserrat",
					}}
					type={"number"}
					domain={[(dataMin: number) => getYAxisRange(dataMin, dataProperty), "auto"]}
					tickSize={4}
					tick={<CustomizedYTick unitType={unitType} />}
					tickLine={{ stroke: "#cccccc" }}
					axisLine={{ stroke: "#cccccc" }}
				/>
				<XAxis
					dataKey="day"
					interval={0}
					tick={<CustomXTick activeValue={activeDay} />}
					label={{
						value: "Days",
						position: "left",
						fill: "#666666",
						fontSize: "12",
						fontWeight: "bold",
						fontFamily: "Montserrat",
						dx: 35,
						dy: 20,
					}}
					tickSize={4}
					axisLine={{ stroke: "#cccccc" }}
					tickLine={{ stroke: "#cccccc" }}
				/>
				<YAxis
					label={{
						value: "Month Pace",
						angle: -90,
						position: "left",
						dx: 10,
						dy: -40,
						fill: "#666666",
						fontSize: "12",
						fontWeight: "500",
						fontFamily: "Montserrat",
					}}
					type={"number"}
					orientation={"right"}
				/>
				{Boolean(filteredRows?.length) && (
					<ReferenceLine
						label={
							<CustomYTick
								text={accumulatedValue({
									idx: activeDay,
									chartIndex,
									data,
									dataProperty,
									tabSelected,
								}).toString()}
								unitType={unitType}
							/>
						}
						y={getReferenceLineValue()}
						stroke="lightblue"
						strokeDasharray={"3 3"}
					/>
				)}
				{Boolean(filteredRows?.length) && <ReferenceLine x={activeDay} stroke="lightblue" strokeDasharray="3 3" />}
				{Boolean(filteredRows?.length) && <ReferenceLine y={0} stroke="#999999" strokeDasharray="3 3" />}
				<Bar
					dataKey={dataProperty}
					shape={
						<CandleStickBar
							dataProperty={dataProperty}
							selected={selectedCommissionCycle}
							lastActiveCommissionCycle={lastActiveCommissionCycle}
							daysInCurrentMonth={daysInCurrentMonth}
							isLastDayOfMonth={isLastDayOfMonth}
							isDateBeforeCurrentDate={isDateBeforeCurrentDate}
						/>
					}
				/>
				{Boolean(!filteredRows?.length) && <Customized component={<NoDataLabel />} />}
				{Boolean(filteredRows?.length) && (
					<ReferenceDot
						x={activeDay}
						y={getReferenceLineValue()}
						isFront
						shape={
							<CustomizedReferenceDot
								text={getValueFromData({
									idx: activeDay - 1,
									data,
									dataProperty,
									chartIndex,
									tabSelected,
								})}
								unitType={unitType}
								transformedData={transformedData}
								isLastDayOfMonth={isLastDayOfMonth}
								activeDay={activeDay}
								daysInCurrentMonth={daysInCurrentMonth}
								dataProperty={dataProperty}
								chartIndex={chartIndex}
								selected={selectedCommissionCycle}
								lastActiveCommissionCycle={lastActiveCommissionCycle}
							/>
						}
					/>
				)}
			</ComposedChart>
		</Stack>
	);
}
